import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import backHeader from "../assets/back-header.png";
import { FaLocationDot } from "react-icons/fa6";

const heroTransitionTypes = ["fade", "slide-left", "slide-right", "square"];

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 3.2 3 10.5v10.3h6.2v-6.4h5.6v6.4H21V10.5L12 3.2z"
      />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M9 6.7 14.3 12 9 17.3l1.4 1.4L17.1 12l-6.7-6.7z"
      />
    </svg>
  );
}

export default function PageBanner({
  image = backHeader,
  imageAlt = "",
  whiteText,
  greenText,
  description,
  buttonText,
  buttonHref,
  showButton = false,
  secondaryButtonText,
  secondaryButtonHref,
  showSearch = false,
  searchPlaceholder,
  searchButtonText,
}) {

  const intl = useIntl();
  const t = (id, values) => intl.formatMessage({ id }, values);
  const navigate = useNavigate();
  const heroSlides = [
    {
      webp: image,
      src: image,
      alt: imageAlt,
      whiteText,
      greenText,
      description,
      buttonText,
      buttonHref,
      secondaryButtonText,
      secondaryButtonHref,
    },
  ];
  const [heroIndex, setHeroIndex] = useState(0);
  const [heroPrevIndex, setHeroPrevIndex] = useState(0);
  const [heroTransition, setHeroTransition] = useState("fade");
  const [isHeroPaused, setIsHeroPaused] = useState(false);
  const heroTimerRef = useRef(null);

  const goToHeroSlide = (updater) => {
    const type = heroTransitionTypes[Math.floor(Math.random() * heroTransitionTypes.length)];
    setHeroTransition(type);
    setHeroIndex(updater);
  };

  useEffect(() => {
    if (isHeroPaused || heroSlides.length <= 1) return;
    heroTimerRef.current = setInterval(() => {
      goToHeroSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(heroTimerRef.current);
  }, [isHeroPaused]);

  useEffect(() => {
    if (heroPrevIndex === heroIndex) return;
    const id = setTimeout(() => setHeroPrevIndex(heroIndex), 900);
    return () => clearTimeout(id);
  }, [heroIndex, heroPrevIndex]);

  const hasMultipleSlides = heroSlides.length > 1;
  return (
    <section className="page-banner">



      <section
                data-testid="eats-hero-slider"
                className="eats-slider"
                onMouseEnter={() => setIsHeroPaused(true)}
                onMouseLeave={() => setIsHeroPaused(false)}
              >
                <picture className="eats-slider__layer">
                  <source type="image/webp" srcSet={heroSlides[heroPrevIndex].webp} />
                  <img src={heroSlides[heroPrevIndex].src} alt={heroSlides[heroPrevIndex].alt} />
                </picture>

                {heroIndex !== heroPrevIndex && (
                  <picture key={heroIndex} className={`eats-slider__layer eats-slider__anim hero-anim-${heroTransition}`}>
                    <source type="image/webp" srcSet={heroSlides[heroIndex]?.webp} />
                    <img src={heroSlides[heroIndex]?.src} alt={heroSlides[heroIndex]?.alt} />
                  </picture>
                )}

                <div className="eats-slider__overlay" aria-hidden="true" />

                <div className="eats-slider__content">
                  <div key={heroIndex} className={`hero-slide-anim hero-anim-${heroTransition}`}>
                     {/* Titre : première ligne blanche + seconde ligne verte METUA'A */}
                     <h1
                      data-testid="core-ui-text"
                      style={{
                        fontFamily: "'RebelSansDisplay', -apple-system, sans-serif",
                        fontWeight: 700,
                        fontSize: "clamp(2.4rem, 5vw, 3.8rem)",
                        lineHeight: 1.1,
                        margin: "0 0 16px 0",
                        color: "#ffffff",
                      }}
                    >
                      {heroSlides[heroIndex]?.whiteText}{" "}
                      {heroSlides[heroIndex]?.greenText ? (
                        <span style={{ color: "#0c6f3f","background-color":"white","padding-left":"4px" ,"padding-right":"4px"}}>{heroSlides[heroIndex]?.greenText}</span>
                      ) : null}
                    </h1>

                    <p
                      data-testid="core-ui-text"
                      style={{
                        fontSize: "0.95rem",
                        lineHeight: 1.6,
                        color: "#ffffff",
                        margin: "0 0 28px 0",
                        fontWeight: 400,
                        textAlign: "justify",
                      }}
                    >
                      {heroSlides[heroIndex]?.description}
                    </p>

                    {showSearch ? (
                      <div className="eats-slider__search">
                        <span className="eats-slider__search-icon">
                          <FaLocationDot />
                        </span>
                        <input type="text" placeholder={searchPlaceholder} />
                        <button type="button">{searchButtonText}</button>
                      </div>
                    ) : null}

                    {showButton && (
                      <div className="hero-btn-group">
                        {heroSlides[heroIndex]?.buttonText && heroSlides[heroIndex]?.buttonHref ? (
                          <a
                            data-testid="core-ui-button"
                            data-tracking-category="Hero Link Button"
                            data-tracking-action="click"
                            data-tracking-label="Homepage > Brand Redesign Jun 2025 > Hero"
                            data-tracking-value={heroSlides[heroIndex]?.buttonText}
                            aria-disabled="false"
                            href={heroSlides[heroIndex]?.buttonHref}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#0c6f3f",
                              color: "#ffffff",
                              border: "none",
                              borderRadius: "48px",
                              padding: "14px 28px",
                              fontSize: "1rem",
                              fontWeight: 600,
                              textDecoration: "none",
                              cursor: "pointer",
                              transition: "background-color 200ms",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#095c34"}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#0c6f3f"}
                          >
                            {heroSlides[heroIndex]?.buttonText}
                          </a>
                        ) : null}

                        {heroSlides[heroIndex]?.secondaryButtonText && heroSlides[heroIndex]?.secondaryButtonHref ? (
                          <a
                            data-testid="core-ui-button"
                            data-tracking-category="Hero Link Button"
                            data-tracking-action="click"
                            data-tracking-label="Homepage > Brand Redesign Jun 2025 > Hero"
                            data-tracking-value={heroSlides[heroIndex]?.secondaryButtonText}
                            aria-disabled="false"
                            href={heroSlides[heroIndex]?.secondaryButtonHref}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "transparent",
                              color: "#ffffff",
                              border: "1.5px solid rgba(255, 255, 255, 0.85)",
                              borderRadius: "48px",
                              padding: "14px 28px",
                              fontSize: "1rem",
                              fontWeight: 600,
                              textDecoration: "none",
                              cursor: "pointer",
                              marginLeft: "12px",
                              transition: "background-color 200ms, color 200ms",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.18)";
                              e.currentTarget.style.color = "#ffffff";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "transparent";
                              e.currentTarget.style.color = "#ffffff";
                            }}
                          >
                            {heroSlides[heroIndex]?.secondaryButtonText}
                          </a>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>


              </section>

    </section>
  );
}
